import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { MarketingEntryPoint } from "./advanced-flow/schemas/marketing/marketing";
import { Provider } from "./order";

/**
 * Structured discount offer type used when calling getCodeForDiscount.
 * This is converted to a string internally for database storage.
 */
export const discountOfferSchema = z.object({
  value: z.number(),
  type: z.enum(["percentage"]),
});
export type DiscountOffer = z.infer<typeof discountOfferSchema>;

export enum TrackingPageDiscountSource {
  GAME = "game",
  UPSELL = "upsell",
}

export enum GeneratedDiscountCodeSourceEnum {
  MARKETING = "marketing",
  TRACKING_PAGE = "tracking_page",
  SHOPPER_AI = "shopper_ai",
  SHOPPER_AGENT = "shopper_agent",
}

export const generatedDiscountCodeSource = [
  GeneratedDiscountCodeSourceEnum.MARKETING,
  GeneratedDiscountCodeSourceEnum.TRACKING_PAGE,
  GeneratedDiscountCodeSourceEnum.SHOPPER_AI,
  GeneratedDiscountCodeSourceEnum.SHOPPER_AGENT,
] as const;
export type GeneratedDiscountCodeSource =
  (typeof generatedDiscountCodeSource)[number];

const baseGeneratedDiscountCodeKeysSchema = z.object({
  discountId: zExt.objectId(),
  customerId: zExt.objectId(),
});

const baseSignupFlowGeneratedDiscountCodeKeysSchema =
  baseGeneratedDiscountCodeKeysSchema.extend({
    entryPoint: createEnum(
      Object.values(MarketingEntryPoint).filter(
        (ep) => ep !== MarketingEntryPoint.SIGNUP_FORM,
      ) as [MarketingEntryPoint, ...MarketingEntryPoint[]],
    ),
    automationId: zExt.objectId().optional(),
    automationExecutionId: zExt.objectId().optional(),
  });

const previewGeneratedDiscountCodeKeysSchema =
  baseGeneratedDiscountCodeKeysSchema.extend({ isPreview: z.literal(true) });
type PreviewGeneratedDiscountCodeKeys = z.infer<
  typeof previewGeneratedDiscountCodeKeysSchema
>;

const nonFormSignupFlowGeneratedDiscountCodeKeysSchema =
  baseSignupFlowGeneratedDiscountCodeKeysSchema.extend({
    entryPoint: createEnum(
      Object.values(MarketingEntryPoint).filter(
        (ep) => ep !== MarketingEntryPoint.SIGNUP_FORM,
      ) as [MarketingEntryPoint, ...MarketingEntryPoint[]],
    ),
  });
type NonFormSignupFlowGeneratedDiscountCodeKeys = z.infer<
  typeof nonFormSignupFlowGeneratedDiscountCodeKeysSchema
>;

const formSignupFlowGeneratedDiscountCodeKeysSchema =
  baseSignupFlowGeneratedDiscountCodeKeysSchema.extend({
    entryPoint: z.literal(MarketingEntryPoint.SIGNUP_FORM),
    formId: zExt.objectId(),
  });
type FormSignupFlowGeneratedDiscountCodeKeys = z.infer<
  typeof formSignupFlowGeneratedDiscountCodeKeysSchema
>;

const campaignGeneratedDiscountCodeKeysSchema =
  baseGeneratedDiscountCodeKeysSchema.extend({ campaignId: zExt.objectId() });
type CampaignGeneratedDiscountCodeKeys = z.infer<
  typeof campaignGeneratedDiscountCodeKeysSchema
>;

const automationGeneratedDiscountCodeKeysSchema =
  baseGeneratedDiscountCodeKeysSchema.extend({
    automationId: zExt.objectId(),
    automationExecutionId: zExt.objectId(),
  });
type AutomationGeneratedDiscountCodeKeys = z.infer<
  typeof automationGeneratedDiscountCodeKeysSchema
>;

const gameVictoryGeneratedDiscountCodeKeysSchema =
  baseGeneratedDiscountCodeKeysSchema.extend({
    orderId: z.string(),
    gameType: z.string(),
    isPreview: z.literal(true).optional(),
  });
type GameVictoryGeneratedDiscountCodeKeys = z.infer<
  typeof gameVictoryGeneratedDiscountCodeKeysSchema
>;

const trackingPageUpsellGeneratedDiscountCodeKeysSchema =
  baseGeneratedDiscountCodeKeysSchema.extend({
    orderId: z.string(),
    trackingPageUpsell: z.string(),
  });
type TrackingPageUpsellGeneratedDiscountCodeKeys = z.infer<
  typeof trackingPageUpsellGeneratedDiscountCodeKeysSchema
>;

const marketingConversationGeneratedDiscountCodeKeysSchema =
  baseGeneratedDiscountCodeKeysSchema.extend({
    marketingConversationId: zExt.objectId(),
  });
type MarketingConversationGeneratedDiscountCodeKeys = z.infer<
  typeof marketingConversationGeneratedDiscountCodeKeysSchema
>;

const shopperConversationGeneratedDiscountCodeKeysSchema =
  baseGeneratedDiscountCodeKeysSchema.extend({
    shopperAiConversationId: zExt.objectId(),
  });
type ShopperConversationGeneratedDiscountCodeKeys = z.infer<
  typeof shopperConversationGeneratedDiscountCodeKeysSchema
>;

const marketingAgentGeneratedDiscountCodeKeysSchema =
  baseGeneratedDiscountCodeKeysSchema.extend({
    discountOffer: z.string(), // e.g., "10%", "20%", "$40"
  });
type MarketingAgentGeneratedDiscountCodeKeys = z.infer<
  typeof marketingAgentGeneratedDiscountCodeKeysSchema
>;

const generatedDiscountCodeKeysSchema = z.union([
  nonFormSignupFlowGeneratedDiscountCodeKeysSchema,
  formSignupFlowGeneratedDiscountCodeKeysSchema,
  campaignGeneratedDiscountCodeKeysSchema,
  automationGeneratedDiscountCodeKeysSchema,
  previewGeneratedDiscountCodeKeysSchema,
  gameVictoryGeneratedDiscountCodeKeysSchema,
  trackingPageUpsellGeneratedDiscountCodeKeysSchema,
  marketingConversationGeneratedDiscountCodeKeysSchema,
  shopperConversationGeneratedDiscountCodeKeysSchema,
  marketingAgentGeneratedDiscountCodeKeysSchema,
]);

export const generatedDiscountCodeSchema = z.object({
  _id: zExt.objectId(),
  discountCode: z.string(),
  teamId: zExt.objectId(),
  createdAt: z.date(),
  updatedAt: z.date(),
  keys: generatedDiscountCodeKeysSchema,
  source: z
    .enum(generatedDiscountCodeSource)
    .default(GeneratedDiscountCodeSourceEnum.MARKETING),
  expiresAt: z.date().optional(),
  externalId: z.string().optional(),
  externalSource: z.enum(Provider).optional(),
  usedAt: z.date().optional(),
  usedOnOrderId: z.string().optional(),
});
export type GeneratedDiscountCode = z.infer<typeof generatedDiscountCodeSchema>;
export type GeneratedDiscountCodeKeys = z.infer<
  typeof generatedDiscountCodeKeysSchema
>;
const partialGeneratedDiscountCodeKeysSchema = z.object({
  discountId: zExt.objectId(),
  customerId: zExt.objectId().optional(),
  formId: zExt.objectId().optional(),
  automationId: zExt.objectId().optional(),
  automationExecutionId: zExt.objectId().optional(),
  campaignId: zExt.objectId().optional(),
  entryPoint: createEnum(MarketingEntryPoint).optional(),
  isPreview: z.literal(true).optional(),
  orderId: z.string().optional(),
  gameType: z.string().optional(),
  trackingPageUpsell: z.string().optional(),
  marketingConversationId: zExt.objectId().optional(),
  shopperAiConversationId: zExt.objectId().optional(),
  discountOffer: z.string().optional(),
});
export type PartialGeneratedDiscountCodeKeys = z.infer<
  typeof partialGeneratedDiscountCodeKeysSchema
>;

export function isNonFormSignupFlowGeneratedDiscountCodeKeys(
  keys: PartialGeneratedDiscountCodeKeys,
): keys is NonFormSignupFlowGeneratedDiscountCodeKeys {
  return !!(
    keys.discountId &&
    keys.customerId &&
    keys.entryPoint &&
    keys.entryPoint !== MarketingEntryPoint.SIGNUP_FORM
  );
}

export function isFormSignupFlowGeneratedDiscountCodeKeys(
  keys: PartialGeneratedDiscountCodeKeys,
): keys is FormSignupFlowGeneratedDiscountCodeKeys {
  return !!(
    keys.discountId &&
    keys.customerId &&
    keys.entryPoint === MarketingEntryPoint.SIGNUP_FORM &&
    keys.formId
  );
}

export function isCampaignGeneratedDiscountCodeKeys(
  keys: PartialGeneratedDiscountCodeKeys,
): keys is CampaignGeneratedDiscountCodeKeys {
  return !!(keys.discountId && keys.customerId && keys.campaignId);
}

export function isAutomationGeneratedDiscountCodeKeys(
  keys: PartialGeneratedDiscountCodeKeys,
): keys is AutomationGeneratedDiscountCodeKeys {
  return !!(
    keys.discountId &&
    keys.customerId &&
    keys.automationId &&
    keys.automationExecutionId
  );
}

export function isPreviewGeneratedDiscountCodeKeys(
  keys: PartialGeneratedDiscountCodeKeys,
): keys is PreviewGeneratedDiscountCodeKeys {
  return !!(keys.discountId && keys.customerId && keys.isPreview);
}

export function isGameVictoryGeneratedDiscountCodeKeys(
  keys: PartialGeneratedDiscountCodeKeys,
): keys is GameVictoryGeneratedDiscountCodeKeys {
  return !!(
    keys.discountId &&
    keys.orderId &&
    keys.gameType &&
    keys.customerId
  );
}

export function isTrackingPageUpsellGeneratedDiscountCodeKeys(
  keys: PartialGeneratedDiscountCodeKeys,
): keys is TrackingPageUpsellGeneratedDiscountCodeKeys {
  return !!(
    keys.discountId &&
    keys.orderId &&
    keys.trackingPageUpsell &&
    keys.customerId
  );
}

export function isMarketingConversationGeneratedDiscountCodeKeys(
  keys: PartialGeneratedDiscountCodeKeys,
): keys is MarketingConversationGeneratedDiscountCodeKeys {
  return !!(keys.discountId && keys.customerId && keys.marketingConversationId);
}

export function isShopperConversationGeneratedDiscountCodeKeys(
  keys: PartialGeneratedDiscountCodeKeys,
): keys is ShopperConversationGeneratedDiscountCodeKeys {
  return !!(keys.discountId && keys.customerId && keys.shopperAiConversationId);
}

export function isMarketingAgentGeneratedDiscountCodeKeys(
  keys: PartialGeneratedDiscountCodeKeys,
): keys is MarketingAgentGeneratedDiscountCodeKeys {
  return !!(keys.discountId && keys.customerId && keys.discountOffer);
}

export function isGeneratedDiscountCodeKeys(
  keys: PartialGeneratedDiscountCodeKeys | undefined,
): keys is GeneratedDiscountCodeKeys {
  if (!keys) {
    return false;
  }
  return (
    isNonFormSignupFlowGeneratedDiscountCodeKeys(keys) ||
    isFormSignupFlowGeneratedDiscountCodeKeys(keys) ||
    isAutomationGeneratedDiscountCodeKeys(keys) ||
    isCampaignGeneratedDiscountCodeKeys(keys) ||
    isGameVictoryGeneratedDiscountCodeKeys(keys) ||
    isTrackingPageUpsellGeneratedDiscountCodeKeys(keys) ||
    isPreviewGeneratedDiscountCodeKeys(keys) ||
    isMarketingConversationGeneratedDiscountCodeKeys(keys) ||
    isShopperConversationGeneratedDiscountCodeKeys(keys) ||
    isMarketingAgentGeneratedDiscountCodeKeys(keys)
  );
}
